/* eslint-disable react/jsx-no-bind */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as Downarrow } from '../../../assets/icons/down-arrow.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as StationIcon } from '../../../assets/icons/sidebar/sidebar-account.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import {
  AvailableMaintenance,
  // FilterScheduleInterFace,
  RequestScheduleInterFace,
} from '../../../interface/partners-maintenance-scheduling.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableMaintenanceManagementFiltersApplied,
  fetchAllMaintenanceSchedules,
  fetchMaintenanceManagementListFilters,
  setScheduleFilterData,
  setSortData,
  updateMaintenanceManagementFilters,
} from '../../../redux/slices/MaintenanceScheduleManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import { RootState } from '../../../redux/store';
import MyButton from '../../../shared-components/button/Button';
import CustomDateRangePicker, { calculateDateRange, normalizePresetKey } from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DropDownArrow from '../../../shared-components/drop-down-menu/DropDownMenu';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import DeviceSidePanel from '../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import FormatUtcString from '../../../shared-functions/FormatUtcString';
import maintenanceScheduleApi from '../../../utils/api/v2/maintenance-schedule';

import MaintenanceScheduling from './MaintenanceScheduling';

/**
 * Management management page
 */
function MaintenanceManagement() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('maintenanceSchedule');
  const { portalType } = usePortal();
  const isPartnerPortal = portalType === PortalType.PARTNER;
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [urlFiltersApplied, setUrlFiltersApplied] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [searchParameters] = useSearchParams();

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
     return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
     };
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  const {
    schedules,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((states: RootState) => states.partnersMaintenanceSchedule);

  /**
   * Fetch the date for using redux function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestScheduleInterFace) => {
    if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchAllMaintenanceSchedules(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    setModalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: schedules,
  });

  /**
   * Handle status change
   * @param {any} rowId - The rowId
   * @param {any} newStatus - The newStatus
   */
  const handleStatusChange = (rowId: string | number | null, newStatus: string) => {
    maintenanceScheduleApi
      .editMaintenanceSchedule(rowId, { status: newStatus })
      .then(() => {
        setModalView({
          title: '',
          header: false,
          rightCorner: '',
          notification: true,
          content: (
            <SuccessNotification
              title={t('statusUpdatedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                fetchData({
                  page,
                  limit,
                  startDate: dateRange.startDate,
                  endDate: dateRange.endDate,
                });
                setOpenStatus(false);
              }}
            />
          ),
        });
        setOpenStatus(true);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * handlePageChange
   * @param {any} value - The value
   */
  const handlePageChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...value,
      limit,
    });
  };
  // /**
  //  * handleLimitChange
  //  */
  // const handleLimitChange = (limitCount: number) => {
  //   fetchData({
  //     ...filter,
  //     page: DefaultApiLimit.page,
  //     limit: limitCount,
  //   });
  // };
  // /**
  //  * Handle the filter submit function
  //  */
  // const handleSubmitFilter = (filterData: FilterScheduleInterFace) => {
  //   dispatch(setScheduleFilterData(filterData));
  //   fetchData({
  //     page: DefaultApiLimit.page,
  //     limit,
  //     ...filterData,
  //   });
  //   setModalBoxStatus('close');
  // };

  // /**
  //  * Handle the filter data clear function
  //  */
  // const filterClearFunction = () => {
  //   if (Object.keys(filter).length > 0) {
  //     dispatch(setScheduleFilterData({}));
  //     fetchData({
  //       page: DefaultApiLimit.page,
  //       limit,
  //     });
  //   }
  // };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setScheduleFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField === 'time' ? 'createdAt' : sortData.sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  // /**
  //  * filter function
  //  */
  // const FilterModal = () => {
  //   setModalBoxStatus('open');
  //   setModalBoxDetails({
  //     title: '',
  //     header: false,
  //     notificationModal: false,
  //     content: (
  //       <MaintenanceFilter
  //         handleCancel={() => setModalBoxStatus('close')}
  //         handleSubmitFilter={handleSubmitFilter}
  //       />
  //     ),
  //   });
  // };

  /**
   * createDropdownList
   * @param {any} maintenanceId - The maintenanceId
   * @param {any} currentStatus - The currentStatus
   */
  const createDropdownList = (maintenanceId: number | string | null, currentStatus?: string) => {
    const allOptions = [
      {
        value: '1', // Changed from 'id' to 'value'
        content: <p style={{ color: 'orange' }}>{t('inProgress')}</p>,
        key: 'inProgress',
        /**
         * createDropdownList
         */
        onClickFunc: () => handleStatusChange(maintenanceId, 'inProgress'),
      },
      {
        value: '2', // Changed from 'id' to 'value'
        content: <p style={{ color: 'green' }}>{t('completed')}</p>,
        key: 'completed',
        /**
         * createDropdownList
         */
        onClickFunc: () => {
          if (currentStatus !== 'inProgress') {
            dispatch(
              setNotificationMessage({
                message: t('pleaseSetInProgressFirst'),
                status: true,
                type: 'error',
                code: 400,
              }),
            );
            return;
          }
          handleStatusChange(maintenanceId, 'completed');
        },
      },
      {
        value: '3', // Changed from 'id' to 'value'
        content: <p style={{ color: 'red' }}>{t('postponed')}</p>,
        key: 'postponed',
        /**
         * createDropdownList
         */
        onClickFunc: () => handleStatusChange(maintenanceId, 'postponed'),
      },
    ];

    if (currentStatus === 'inProgress') {
      return allOptions.filter((option) => option.key === 'completed');
    }

    return allOptions;
  };

  const column = [
    {
      label: t('maintenanceStart', 'Maintenance Start'),
      accessorKey: 'startDateUtc' as keyof AvailableMaintenance,
      sortAllow: true,
      /**
       * customData
       * @param {any} maintenance - The maintenance
       */
      customData: (maintenance: AvailableMaintenance) => (
        <div className="maintenance-management__row-data">
          {maintenance.startDate} {maintenance.startTime}
        </div>
      ),
    },
    {
      label: t('maintenanceEnd', 'Maintenance End'),
      accessorKey: 'endDateUtc' as keyof AvailableMaintenance,
      sortAllow: true,
      /**
       * customData
       * @param {any} maintenance - The maintenance
       */
      customData: (maintenance: AvailableMaintenance) => (
        <div className="maintenance-management__row-data">
          {maintenance.endDate} {maintenance.endTime}
        </div>
      ),
    },
    // {
    //   label: t('facilityName'),
    //   accessorKey: 'accountName' as keyof AvailableMaintenance,
    //   sortAllow: true,
    // },
    {
      label: t('facilityAccountId'),
      accessorKey: 'accountCustomId' as keyof AvailableMaintenance,
      sortAllow: true,
      ...(!isPartnerPortal
        ? {
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
                onRefreshList={handleRefresh}
              />
            ),
            disableNewTab: true,
          }
        : {}),
    },
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof AvailableMaintenance,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('stationLocation'),
      accessorKey: 'stationLocation' as keyof AvailableMaintenance,
      sortAllow: false,
      /**
       * maintenance
       * @param {any} maintenance - The maintenance
       */
      customData: (maintenance: AvailableMaintenance) => (
        <div className="maintenance-management__location">
          <span>
            <LocationIcon />
          </span>
          <p className="maintenance-management__row-data">{maintenance.address}</p>
        </div>
      ),
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingDeviceName' as keyof AvailableMaintenance,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPoint',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('task'),
      accessorKey: 'type' as keyof AvailableMaintenance,
      sortAllow: false,
    },
    {
      label: t('duration'),
      accessorKey: 'duration' as keyof AvailableMaintenance,
      sortAllow: true,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof AvailableMaintenance,
      sticky: 'right' as const,
      /**
       * maintenance
       * @param {any} maintenance - The maintenance
       */
      customData: (maintenance: AvailableMaintenance) => {
        const statusText =
          createDropdownList(maintenance.id).find((value) => value.key === maintenance.status)
            ?.content || t(maintenance.status as any);

        if (maintenance.status === 'completed' || maintenance.status === 'postponed') {
          return <div className="status-wrapper-dropdown">{statusText}</div>;
        }

        return (
          <DropDownArrow
            rowId={maintenance.id?.toString() || ''} // Convert id to string
            dropDownIcon={
              <div className="status-wrapper-dropdown">
                {statusText}
                <Downarrow />
              </div>
            }
            dropDownList={createDropdownList(maintenance.id, maintenance.status)}
            lineBetweenElement
          />
        );
      },
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('maintenanceStart', 'Maintenance Start'), accessorKey: 'startDateUtc' },
    { label: t('maintenanceEnd', 'Maintenance End'), accessorKey: 'endDateUtc' },
    { label: t('facilityAccountId'), accessorKey: 'accountCustomId' },
    { label: t('stationName'), accessorKey: 'name' },
    { label: t('stationLocation'), accessorKey: 'stationLocation' },
    { label: t('deviceName'), accessorKey: 'chargingDeviceName' },
    { label: t('task'), accessorKey: 'type' },
    { label: t('duration'), accessorKey: 'duration' },
    { label: t('status'), accessorKey: 'status' },
    { label: t('created', 'Created'), accessorKey: 'createdAt' },
    { label: t('lastUpdated', 'Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    maintenanceScheduleApi
      .fetchMaintenanceScheduleExport(
        removeEmptyKeys({
          ...queryParameters,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          sortField,
          sortBy,
        }),
      )
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableMaintenance, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'startDateUtc':
                return row.startDateUtc
                  ? `${ConvertUTCtoDate(row.startDateUtc)} ${FormatUtcString(ConvertUTCtoHHMM(row.startDateUtc))}`
                  : 'N/A';
              case 'endDateUtc':
                return row.endDateUtc
                  ? `${ConvertUTCtoDate(row.endDateUtc)} ${FormatUtcString(ConvertUTCtoHHMM(row.endDateUtc))}`
                  : 'N/A';
              case 'name':
                return row.chargingStationName;
              case 'stationLocation':
                return row.chargingStationAddress;
              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt || '');
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt || '');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${'maintenanceScheduleList'}.csv`);
          } else {
            exportPDF(headers, rows, `${'maintenanceScheduleList'}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Add maintenance schedule function
   */
  const addMaintenanceSchedule = () => {
    setSidePanelOpen(true);
  };

  useEffect(() => {
    if (!urlFiltersApplied) return;

    if (isFilterUpdated || schedules.length === 0 || dateRange.startDate || dateRange.endDate) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });

      dispatch(disableMaintenanceManagementFiltersApplied());
    }
  }, [filters, urlFiltersApplied, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('stationId');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('maintenanceSchedule'),
          icon: <StationIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('maintenanceSchedule'),
          icon: <StationIcon />,
          count: undefined,
        }),
      );
    }
  }, [totalResults, searchParameters, t]);

  useEffect(() => {
    const stationName = searchParameters.get('stationName');
    const status = searchParameters.get('status');
    const startDate = searchParameters.get('startDate');
    const endDate = searchParameters.get('endDate');

    if (!status && !startDate && !endDate && !stationName) {
      setUrlFiltersApplied(true);
      return;
    }

    /**
     * Function to apply filters from URL parameters
     */
    const applyFilters = () => {
      // Clear filters once if any URL parameter exists
      dispatch(
        updateMaintenanceManagementFilters({
          key: '',
          value: '',
          type: 'clear',
        }),
      );

      if (status) {
        dispatch(
          updateMaintenanceManagementFilters({
            key: 'status',
            value: status,
            type: 'add',
          }),
        );
      }

      // if (startDate) {
      //   dispatch(
      //     updateMaintenanceManagementFilters({
      //       key: 'dateRange',
      //       subKey: 'startDate',
      //       value: startDate,
      //       type: 'add',
      //     }),
      //   );
      // }

      // if (endDate) {
      //   dispatch(
      //     updateMaintenanceManagementFilters({
      //       key: 'dateRange',
      //       subKey: 'endDate',
      //       value: endDate,
      //       type: 'add',
      //     }),
      //   );
      // }
      if(startDate || endDate) {
        setDateRange({
          startDate: startDate || '',
          endDate: endDate || '',
        });
      }

      if (stationName) {
        dispatch(
          updateMaintenanceManagementFilters({
            key: 'stationName',
            value: stationName,
            type: 'add',
          }),
        );
      }

      setUrlFiltersApplied(true);
    };

    if (filters.length === 1) {
      // filters list is not yet loaded (only searchBy exists)
      dispatch(fetchMaintenanceManagementListFilters()).then(() => {
        applyFilters();
      });
    } else {
      applyFilters();
    }
  }, [searchParameters, filters.length, dispatch]);

  return (
    <div className="maintenance-management">
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        notification={modalView.notification}
        header={modalView.header}
      />
      <div className="maintenance-management__table-container">
        <CustomTable
          dataKey="MaintenanceManagement"
          loading={loading}
          limit={limit}
          header={column}
          data={schedules}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          page={page}
          serialNo
          created
          lastUpdated
          handleSortChange={handleSortFunction}
          filters={filters}
          filtersToDispatch={updateMaintenanceManagementFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchMaintenanceManagementListFilters}
          pageLimitChange={handlePageChange}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refreshRequired
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          onHiddenColumnsChange={setHiddenColumns}
          rightTopContent={
            <div className="table-bottom-button-list">
              {renderDateFilters()}
              <div className="right-action">
                <MyButton
                  label={t('addMaintenanceSchedule')}
                  buttonType="submit"
                  onClickFunc={addMaintenanceSchedule}
                  svgIcon={<PlusIcon />}
                />
              </div>
            </div>
          }
          onClearFilterChange={() => {
            clearDateRange();
          }}
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={
            <MaintenanceScheduling
              onClose={() => setSidePanelOpen(false)}
              onSuccess={handleRefresh}
            />
          }
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          version="v2"
          onSidePanelClose={() => {
            handleSidePanelClose();
            setSidePanelOpen(false);
          }}
          sizeChangeButtonRequired
        />
      </div>
    </div>
  );
}

export default MaintenanceManagement;
